import { IFlightDelay } from "@vmosolutions/oc-interfaces/dist/flights/IFlightDelay";
import React, { useEffect, useMemo, useState } from "react";
// Material UI Styling
import { alpha, Button, Grid, GridProps, styled, Typography } from "@mui/material";
import {
  DelayCodeControllableOptions,
  IAircraft,
  IATA,
  IATACode,
  IDelay,
  IDelayCategory,
  IDelayCode,
  IFullFlight,
} from "@vmosolutions/oc-interfaces";
import { useSelector } from "react-redux";
import { FilterSearchTable } from "src/components/FilterSearchTable/FilterSearchTable";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { sortDelayOptions } from "src/utils/delayCode";
import { defaultDelayValues, EditAddFlightDelay } from "./EditAddFlightDelay";
import { FlightDelay } from "./FlightDelay";
/////////////
/// TABLE ///
/////////////

const delaysTableInfo = [
  {
    id: "onDeparture",
    value: "onDeparture",
    index: 1,
    numeric: true,
    label: "Delay",
    type: "string",
    width: 10,
    visible: true,
    filter: true,
  },
  {
    id: "duration",
    value: "duration",
    index: 2,
    numeric: true,
    label: "Duration (mins)",
    type: "number",
    width: 10,
    visible: true,
    filter: true,
  },
  {
    id: "delay[delayCode]", //delay[delayCode][title]
    value: "delay[delayCode]",
    index: 3,
    numeric: true,
    label: "Delay Code",
    type: "string",
    width: 10,
    visible: true,
    filter: true,
  },
  {
    id: "ATA",
    value: "ATA",
    index: 4,
    numeric: true,
    label: "ATA Code(s)",
    type: "object",
    width: 10,
    visible: true,
    filter: true,
  },
  {
    id: "notes",
    value: "notes",
    index: 5,
    numeric: true,
    label: "Notes",
    type: "string",
    width: 10,
    visible: false,
    filter: false,
  },
  {
    id: "aircraftRegistration",
    value: "aircraftRegistration",
    index: 6,
    numeric: true,
    label: "Aircraft",
    type: "string",
    width: 10,
    visible: false,
    filter: true,
  },
  {
    id: "otherDesc",
    value: "otherDesc",
    index: 7,
    numeric: true,
    label: "Other Description",
    type: "string",
    width: 10,
    visible: false,
    filter: false,
  },
];

export interface IDelayOptions {
  // value: string;
  // label: string;
  // requireATA: boolean;
  // requireOther: boolean;
  // requireTail: boolean;

  _id: any;
  _old_id: string;
  title: string;
  description: string;
  delayCode: {
    _id: any;
    _old_id: number;
    delayNumber: number;
    title: string;
    description: string;
    division: string;
    requireATA: boolean;
    requireOther: boolean;
    requireTail: boolean;
    controllable: DelayCodeControllableOptions | boolean;
  };
}

export interface IAtasOptions {
  _id: any;
  ataNumber: string;
  ataChapterName: string;
  ataCode: IATACode;
}

export interface IAircraftOptions {
  value: string;
  label: string;
}

export interface ITableFormatDelay {
  _id: string;
  onDeparture: string;
  delay: IDelay;
  ATA?: {
    _id: any;
    ataNumber: string;
    ataChapterName: string;
    ataCode: IATACode;
  }[];
  duration: string;
  notes: string;
  otherDesc: string;
  aircraft_id: string;
  aircraftRegistration: string;
  start?: string;
  end?: string;
}

const OCDelaysForm = styled((props: GridProps) => <Grid {...props} />)(({ theme }) => ({
  margin: 10,
  width: "calc(100% - 20px)",
  "& .delay_table": {
    padding: 5,
    overflow: "auto",
    width: "100%",
  },
  "& .edit_addflightdelay": {
    padding: 10,
    width: "100%",
    "& .delay_form": {
      "& .header ": {
        paddingLeft: 10,
      },
      "& .required_fields": {
        backgroundColor: alpha(theme.custom.forms.labels, 0.2),
        margin: "2px 0px",
        display: "flex",
        flexWrap: "wrap",
        "& .error_text": {
          paddingLeft: 5,
          "& .error": {
            color: theme.palette.error.main,
          },
        },
      },

      "& .checkbox_departure, & .checkbox_arrival, & .duration_textfield": { width: 120 },
      "& .delay_code_selection, & .ata_code_selection, & .other_description_field, & .note_field": { width: "100%" },
      "& .aircraft_selection": { width: 263 },

      "& .buttons": {
        display: "flex",
        width: "100%",
        justifyContent: "flex-start",
      },
    },
  },
}));

const convertToTableFormat = (delay: IFlightDelay, aircraftOptions: IAircraftOptions[]): ITableFormatDelay => {
  const formattedDelay: ITableFormatDelay = {
    _id: delay._id,
    onDeparture: delay.onDeparture ? "Departure" : "Arrival",
    delay: delay.delay ?? null,
    ATA: delay.ATA,
    duration: delay.duration?.toString(), //`${moment(delay.end).diff(moment(delay.start), "minutes")} minutes`,
    notes: delay.notes,
    otherDesc: delay.otherDesc,
    aircraft_id: delay.aircraft_id ? delay.aircraft_id : "None",
    aircraftRegistration: delay.aircraft_id
      ? aircraftOptions.find((option: IAircraftOptions) => option.value === delay.aircraft_id)?.label
      : "None",
  };

  return formattedDelay;
};

export interface IDelaysFormProps {
  flightId: string;
  delays: IFlightDelay[];
  actualOut: number;
  actualIn: number;
  scheduledOut: number;
  scheduledIn: number;
  airportIdent: string[];
  selectedAircraft?: string;
  windowId?: string;
  departDelayDuration: number;
  arrivalDelayDurtaion: number;
  setLoadedFlight: (flightValues: IFullFlight) => void;
  updateTabState?: (dirtyStatus: boolean) => void; // update parent with dirty status.
}

// Function used to create the dataSet needed for the table component.
const createTableData = (delays: ITableFormatDelay[]): any => {
  return [
    {
      id: "delaysDetails",
      dataType: "delaysDetails",
      label: "Delay Details",
      data: delays,
      tableInfo: delaysTableInfo,
      tableInfoDisplay: {
        tableTitle: "Detail Entries",
        tableDescription: "Select the delay you would like to edit",
        NoDataMessage: "No delays to display.",
      },
    },
  ];
};

export const DelaysForm = (props: IDelaysFormProps): JSX.Element => {
  const {
    windowId,
    delays,
    airportIdent,
    flightId,
    departDelayDuration,
    arrivalDelayDurtaion,
    actualOut,
    actualIn,
    scheduledOut,
    scheduledIn,
    setLoadedFlight,
    updateTabState,
  } = props;

  // If true, will display delay form with table underneath to add or edit existing.
  const [displayForm, setDisplayForm] = useState<boolean>(false);

  const [delayOptions, setDelayOptions] = useState<any>([]);
  const [atasOptions, setAtasOptions] = useState<any>([]);
  const [aircraftOptions, setAircraftOptions] = useState<IAircraftOptions[]>([]);
  const [selectedDelay, setSelectedDelay] = useState<IFlightDelay>(defaultDelayValues);
  //structures
  const structures = useSelector((state: IRootState) => state.structures.customerStructure);
  const { ATAs, aircraft, delayCategories } = structures;
  const [dataSetEvents, setDataSetEvents] = React.useState<any>(createTableData([]));
  const uiSettings = useSelector((state: IRootState) => state.UISettings);
  const { delayCodeFormat, delayCodeSortOrder } = uiSettings;
  // create delayOptions array onmount
  useEffect(() => {
    if (delayCategories) {
      const tempDelayOptions: IDelayOptions[] = [];
      delayCategories.forEach((cat: IDelayCategory) => {
        cat.delayCodes.forEach((opt: IDelayCode) => {
          tempDelayOptions.push({
            _id: cat._id,
            _old_id: cat._old_id,
            title: cat.title,
            description: cat.description,
            delayCode: opt,
          });
        });
      });
      setDelayOptions(sortDelayOptions(tempDelayOptions, delayCodeFormat, delayCodeSortOrder));
    }
  }, [delayCategories]);

  // create atasOptions array onmount
  useEffect(() => {
    if (ATAs) {
      const tempAtasOptions: IAtasOptions[] = [];
      ATAs.forEach((ata: IATA) => {
        ata.ataCodes.forEach((opt) => {
          // const code = ata.ataNumber.concat(opt.code)
          tempAtasOptions.push({
            // value: opt._id,
            // label: code.concat("-", opt.description),
            _id: ata._id,
            ataNumber: ata.ataNumber,
            ataChapterName: ata.ataChapterName,
            ataCode: {
              _id: opt._id,
              code: opt.code,
              description: opt.description,
            },
          });
        });
      });

      setAtasOptions(
        tempAtasOptions.sort((a: IAtasOptions, b: IAtasOptions) =>
          a.ataNumber === b.ataNumber
            ? a.ataCode.code.localeCompare(b.ataCode.code)
            : a.ataNumber.localeCompare(b.ataNumber)
        )
      );
    }
  }, [ATAs]);

  // create aircraftOptions array onmount
  useEffect(() => {
    if (aircraft) {
      const tempAircraftOptions: IAircraftOptions[] = aircraft.map((aircraft: IAircraft) => {
        return {
          value: aircraft?._id,
          label: aircraft?.registration,
        };
      });
      setAircraftOptions(tempAircraftOptions);
    }
  }, [aircraft]);

  useEffect(() => {
    const tableData: ITableFormatDelay[] = delays.map((delay: IFlightDelay) =>
      convertToTableFormat(delay, aircraftOptions)
    );
    setDataSetEvents(createTableData(tableData));
  }, [delays, aircraftOptions]);

  const depDelays = useMemo(() => delays?.filter((delay) => delay?.onDeparture), [delays]);
  const arrDelays = useMemo(() => delays?.filter((delay) => !delay?.onDeparture), [delays]);

  return (
    <OCDelaysForm container direction="column">
      <Grid className="delay_form" container style={{ width: "auto" }}>
        <Button
          disabled={!displayForm}
          onClick={() => setDisplayForm(false)}
          color="primary"
          variant="outlined"
          autoFocus={true}
          style={{ padding: 5, height: 24, fontSize: 12, margin: 5 }}
        >
          Show List
        </Button>
        <Button
          disabled={displayForm}
          onClick={() => setDisplayForm(true)}
          color="primary"
          variant="outlined"
          autoFocus={true}
          style={{ padding: 5, height: 24, fontSize: 12, margin: 5 }}
        >
          Add/Update
        </Button>
      </Grid>

      {displayForm ? (
        <Grid container direction="column">
          <Grid item className="edit_addflightdelay">
            <EditAddFlightDelay
              flightId={flightId}
              delayOptions={delayOptions}
              atasOptions={atasOptions}
              aircraftOptions={aircraftOptions}
              windowId={windowId}
              airportIdent={airportIdent ? airportIdent : null}
              actualOut={actualOut}
              actualIn={actualIn}
              scheduledOut={scheduledOut}
              scheduledIn={scheduledIn}
              departDelayDuration={departDelayDuration}
              arrivalDelayDurtaion={arrivalDelayDurtaion}
              fullArrayDelays={delays}
              selectedDelay={selectedDelay}
              setSelectedDelay={setSelectedDelay}
              setLoadedFlight={setLoadedFlight}
              updateTabState={updateTabState}
            />
          </Grid>

          <Grid item className="delay_table">
            <FilterSearchTable
              id={props.windowId}
              className="flight_delays"
              events={dataSetEvents}
              parentId={`${windowId}-panel`}
              uniqueKey={"_id"}
              hideFilter={true}
              hideFocusSelected={true}
              selectModeSingle={true}
              changeSelectedRows={(selectedRow: any) => {
                if (selectedRow.length === 1) {
                  setSelectedDelay(selectedRow[0]);
                } else {
                  setSelectedDelay(defaultDelayValues);
                }
              }}
              // handleAction={handleAction}
              // actions={actions}
            />
          </Grid>
        </Grid>
      ) : (
        <Grid container direction="column" className="delay_list" style={{ minHeight: 300, width: "100%" }}>
          {depDelays?.length > 0 ? (
            <Grid container direction="column" className="departure_delay_list" style={{ marginTop: 10 }}>
              <Typography variant="caption">{`Departure Delays (count: ${depDelays?.length})`}</Typography>
              {depDelays
                .sort((a, b) => b.duration - a.duration)
                .map((delay) => {
                  const AC =
                    delay?.aircraft_id && aircraft?.length
                      ? aircraft?.find((AC) => AC._id === delay?.aircraft_id)
                      : null;
                  return (
                    <Grid item key={`delay_${delay._id}`}>
                      <FlightDelay flightDelay={delay} aircraft={AC} />
                    </Grid>
                  );
                })}
            </Grid>
          ) : null}

          {arrDelays?.length > 0 ? (
            <Grid container direction="column" className="arrival_delay_list" style={{ marginTop: 10 }}>
              <Typography variant="caption">{`Arrival Delays (count: ${arrDelays?.length})`}</Typography>
              {arrDelays
                .sort((a, b) => b.duration - a.duration)
                .map((delay) => {
                  const AC =
                    delay?.aircraft_id && aircraft?.length
                      ? aircraft?.find((AC) => AC._id === delay?.aircraft_id)
                      : null;
                  return (
                    <Grid item key={`delay_${delay._id}`}>
                      <FlightDelay flightDelay={delay} aircraft={AC} />
                    </Grid>
                  );
                })}
            </Grid>
          ) : null}
        </Grid>
      )}
    </OCDelaysForm>
  );
};
